/*globals $:false */

import ClipboardP from './presenter/recordm.clipboard.js';
import { DEV_MODE } from '@cob/ui-genesis/helpers/cob.devmode.js';
import { InfoDialog } from '@cob/ui-genesis/helpers/cob.dialog.js';
import InstanceM from './model/recordm.model.entity.instance.js';
import InstanceP from './presenter/recordm.presenter.instance.js';
import LogPresenter from '@cob/ui-genesis/modules/cob.log.js';

import { sprintf } from 'sprintf-js';

import tmpl_container from './templates/instance-container.dot';
import tmpl_json_log_entry from './templates/log-entry-json.dot';

import utils from '@cob/ui-genesis/cob.utils.js';

/**
 * args:
 * * `core`: cob.app
 * * `opts`: accepts:
 *       * `container`: an element that should contain the UI
 *       * `exit`: a callback called when the user has asked to exit the Instance creation
 *       * `afterSave`: a callback called after saving the instance
 *       * `exitLabel`: a localize key to be used on the exit (back) button
 */
var InstanceDetail = function(core, opts) {

    var _$mainContainer,
        _clipboardP,
        _instanceP,
        _logPresenter,
        _running = false;

    var _urlData,
        _instanceId,
        _instanceM;

    const _opts = {...opts};

    var _loadingKey = core.getLoadingKey();

    var _loadInstance = function (instanceId, instanceLoadLatch) {

        core.showLoading(_loadingKey, "_loadInstance");

        InstanceM.get(core, instanceId, {
            forceReload: true,

            success: function(instanceM) {
                _instanceM = instanceM;

                core.publish("modules:stack-description-updated");
                instanceLoadLatch.countDown("instance:loaded:" + instanceM.data.id);

                core.hideLoading(_loadingKey, "_loadInstance.success");
            },

            error: function(status, req) {
                if (status === 404) {
                    InfoDialog(core, {
                        title: sprintf(core.translateString("recordm.instances", "msgs.error-title"), instanceId),
                        message: core.translateString("recordm.instances", "msgs.not-found"),
                        closeBtnLabel: core.translateString("recordm.instances", "action.back")
                    }, {
                        onClose: () => {  core.navigateBack() }
                    })
                }

                if (status === 503 && req.responseJSON.message === 'auto.extRef.timeout') {
                   InfoDialog(core, {
                      title: sprintf(core.translateString("recordm.instances", "msgs.error-title"), instanceId),
                      message: sprintf(core.translateString("recordm.instances", "msgs.ext-ref-timeout"), req.responseJSON.productKey),
                      closeBtnLabel: core.translateString("recordm.instances", "action.back")
                   }, {
                      onClose: () => {  core.navigateBack() }
                   })
                }

                core.hideLoading(_loadingKey, "_loadInstance.error");
            }
        });
    };

    var _init = function (instanceId, urlData, opts) {
        _running = true;
        _instanceId = instanceId || _instanceId;
        _urlData = urlData || _urlData;

        // Creating a latch because the code becomes more
        // simple to read. This will happen after the domain has been loaded
        // successfully.
        var instanceLoadLatch = new utils.CountdownLatch(1, function () {

            core.showLoading(_loadingKey, "instanceLoadLatch");

            _$mainContainer = $(tmpl_container({
                containerClass: "js-instance-details-container"
            }));

            core.translate("recordm.instances", _$mainContainer);

            if(_running){
                if(_opts.container){
                    DEV_MODE && window.console.debug('JB', 'appending to container')
                    _$mainContainer.appendTo(_opts.container);
                } else {
                    _$mainContainer.appendTo('#'+core.getAppInstanceId());
                }
            }
            _$mainContainer.removeClass("hidden");

            _clipboardP = new ClipboardP(core);
            _clipboardP.init(urlData);

            const _callbacks = {
                cancel: function(){
                    if(typeof _opts.exit == 'function') _opts.exit();
                    else core.navigateBack();
                },
                afterSave: function(){
                    if(typeof _opts.afterSave == 'function') _opts.afterSave();
                    else setTimeout(() => core.navigateBack(), 300);
                }
            };

            if (typeof _opts.afterSaveEdit == 'function') {
                _callbacks.afterSaveEdit = _opts.afterSaveEdit;
            }
            if (typeof _opts.afterSaveCreateNew == 'function') {
                _callbacks.afterSaveCreateNew = _opts.afterSaveCreateNew;
            }

            _instanceP = new InstanceP(core, _$mainContainer, _instanceM, _clipboardP, _callbacks, {
                exitLabel: _opts.exitLabel
            });

            _instanceP.init(false, false);

            //no history when showing inline
            if(!_opts.container){
                // The log is being initialized after the instance has been shown
                // because the log container is inside the instance template.
                _$mainContainer.find(".js-log-container").removeClass("hidden");

                _logPresenter = new LogPresenter(core, $(".js-log-wrapper2", _$mainContainer), {
                    skipLocalize:true,

                    prepare: function(logEntry){
                        var jsonMsg;

                        if(logEntry.message.startsWith('{')){
                            jsonMsg = JSON.parse(logEntry.message);

                            /* fazemos JSON.parse porque as mensagens de update de defs vêm com json dentro do text */
                            if(jsonMsg.text && jsonMsg.text.startsWith('{')){
                               jsonMsg.text = JSON.parse(jsonMsg.text).text;
                            }
                            Object.keys(jsonMsg).forEach((changeType) => {
                               if(changeType !== 'text'){
                                  jsonMsg[changeType].forEach((value) => {
                                     if(value.type === 'DateTime'){
                                        if(value.before){
                                           try {
                                              value.before = utils.time.getLocalDateTimeFromTimeStamp(Number(value.before), core.getLocale());
                                           } catch (e) {
                                              window.console.error("couldn't parse timestamp %s: ", value.before, e)
                                           }
                                        }
                                        if(value.after){
                                           try {
                                              value.after = utils.time.getLocalDateTimeFromTimeStamp(Number(value.after), core.getLocale());
                                           } catch (e) {
                                              window.console.error("couldn't parse timestamp %s: ", value.after, e)
                                           }
                                        }

                                     } else if (value.type === 'Date'){
                                        if(value.before){
                                           try {
                                              value.before = utils.time.getLocalDateFromTimeStamp(Number(value.before), core.getLocale());
                                           } catch (e) {
                                              window.console.error("couldn't parse timestamp %s: ", value.before, e)
                                           }
                                        }
                                        if(value.after){
                                           try {
                                              value.after = utils.time.getLocalDateFromTimeStamp(Number(value.after), core.getLocale());
                                           } catch (e) {
                                              window.console.error("couldn't parse timestamp %s: ", value.after, e)
                                           }
                                        }

                                     } else {
                                        value.before = utils.encodeHTML(value.before);
                                        value.after = utils.encodeHTML(value.after);
                                     }
                                  })
                               }
                            })

                            logEntry.message_original = jsonMsg;
                            logEntry.message = tmpl_json_log_entry(jsonMsg);
                        }
                    }
                });
                _logPresenter.init("instance", _instanceId);
            }
            core.hideLoading(_loadingKey, "instanceLoadLatch.done");

            opts?.onDone?.()
        });

        _loadInstance(_instanceId, instanceLoadLatch);

    };

    var _destroy = function() {
        _running = false;

        if(_$mainContainer){
            _$mainContainer.remove();
        }

        if (_instanceP) {
            _instanceP.destroy();
        }

        if (_logPresenter) {
            _logPresenter.stop();
        }

        if (_instanceM) {
            _instanceM.release();
        }
    };

    return {
        init: _init,
        pause: _destroy,
        resume: _init,
        destroy: _destroy,

        getInstanceP: function () {
            return _instanceP;
        },

        getInstanceM: function () {
            if (_instanceM) {
                return _instanceM;
            } else {
                return {};
            }
        },

        getDescription: function () {
            if (_instanceM) {
                return {
                    definition: {
                        id: _instanceM.data.jsonDefinition.id,
                        name: _instanceM.data.jsonDefinition.name,
                        description: _instanceM.data.jsonDefinition.description
                    }
                }

            } else {
                return {};
            }
        }
    };
};

export default InstanceDetail;
